import { subscriberSelectors } from './../../subscription/subscriber/subscriber.selectors'
import { subscriptionSelectors } from './../../subscription/subscription.selectors'
import { Participation } from 'src/app/features/booking/participation/participation'
import { bookingSelectors } from 'src/app/features/booking/booking.selectors'
import { productSelectors } from './../../product/product.selectors'
import { saleItemSelectors } from './../sale-item/sale-item.selectors'
import { SaleItem } from 'src/app/features/sale/sale-item/sale-item'
import { persistentUserRoleSelectors } from 'src/app/features/role/user-role/persistent-user-role/persistent-user-role.selector'
import { settingSelectors } from './../../../core/settings/settings.selectors'
import {
  AccessRights,
  UserRole
} from 'src/app/features/role/user-role/user-role'
import { environment } from './../../../../environments/environment'
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  map,
  take,
  switchMap,
  filter,
  catchError,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { Dictionary } from '@ngrx/entity'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { authSelectors } from '../../../core/auth/auth.selectors'
import { AppState } from '../../../core/core.state'
import { NotificationService } from '../../../core/notification.service'
import {
  getEnumAsArray,
  scrollToFirstInvalidControl
} from '../../../shared/until'
import { Member } from '../../member/member'
import { memberActions } from '../../member/member.actions'
import { memberSelectors } from '../../member/member.selectors'
import { Organization } from '../../organization/organization'
import { organizationSelectors } from '../../organization/organization.selectors'
import { Sale, SalePatchActions, StatusCode } from '../sale'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { userRoleActions } from '../../role/user-role/user-role.actions'
import { Role } from '../../role/role'
import { roleSelectors } from '../../role/role.selectors'
import { paymentSelectors } from '../payment/payment.selectors'
import { saleSelectors } from '../sale.selectors'
import { SaleService } from '../sale.service'
import { saleActions } from '../sale.actions'
import { Actions, ofType } from '@ngrx/effects'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { ConfigurationService } from 'src/app/shared/services/configuration.service'
import { Product } from '../../product/product'
import { Booking } from '../../booking/booking'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { Subscription } from '../../subscription/subscription'
import { Subscriber } from '../../subscription/subscriber/subscriber'
import { subscriberActions } from '../../subscription/subscriber/subscriber.actions'
import { Payment, PayMethodType } from '../payment/payment'
import { TranslateService } from '@ngx-translate/core'
import { ConfigOptionsKeys } from 'src/app/shared/constants'

interface Customer {
  id: string
  firstName: string
  lastName: string
}

@UntilDestroy()
@Component({
  selector: 'sale-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class SaleFormComponent implements OnInit, OnChanges, AfterViewInit {
  @Output() onSubmit = new EventEmitter<any>()
  @Input() title: string
  @Input() sale: Sale
  @Input() showSubmitButton: boolean = true
  @Input() triggerSubmit$: Observable<boolean>
  @Input() showSaleStatus: boolean = false
  @Input() isCreating: boolean = false
  @Input() readonly: boolean = false
  public saleStatusCode = StatusCode
  public mappedCategoryIdArray: any[] = []
  public memberCategory: number[]
  public defaultSelected: number
  public selectedOrganization: string
  public customers: Customer[] = []
  public selectedMember: Member
  public changeCustomer: boolean = false
  public form: UntypedFormGroup
  public saleStatus = getEnumAsArray(StatusCode)
  public members$: Observable<Member[]>
  public organizationEntities$: Observable<Dictionary<Organization>>
  public userRoles$: Observable<UserRole[]>
  public roleEntities$: Observable<Dictionary<Role>>
  public userByRoles$ = this.store.select(userRoleSelectors.selectsearchedUsers)
  public userRolesLoading$ = this.store.select(
    userRoleSelectors.selectIsLoading
  )
  public isUpdatingPrices: boolean = false
  public organizationModuleConfig: any
  public accessRights = AccessRights
  public saleModuleConfig = environment.moduleConfigs.saleModule

  public currentUserRole$: Observable<UserRole>
  public selectedLanguage$: Observable<string>
  public categories$: Observable<string[]>
  public internalNotesChange: boolean = false
  public orderNumbersChange: boolean = false
  public payments$: Observable<Dictionary<Payment>>
  public saleItems$: Observable<Dictionary<SaleItem>>
  public products$: Observable<Dictionary<Product>>
  public bookings$: Observable<Dictionary<Booking>>
  public participations$: Observable<Dictionary<Participation>>
  public subscriptions$: Observable<Dictionary<Subscription>>
  public subscribers$: Observable<Subscriber[]>
  public totalNewSale: number
  public saleHaveUndecidedPayments$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false)
  public multidayEnabled$: Observable<boolean>
  private currentUserRole: UserRole

  public isLoading$: Observable<boolean>

  constructor(
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private saleService: SaleService,
    private configService: ConfigurationService,
    private actions$: Actions,
    private translate: TranslateService
  ) {}

  ngAfterViewInit(): void {
    if (!this.sale?.organizationId) {
      return
    }
    this.form.controls.organizationId.setValue(this.sale.organizationId)

    this.store
      .select(saleSelectors.selectPartial)
      .pipe(take(1))
      .subscribe((sale) => {
        const customerCategory = sale?.customerCategory
        if (
          customerCategory !== undefined &&
          (customerCategory === null || customerCategory > -1)
        ) {
          this.form.controls['customerCategory'].patchValue(
            this.defaultSelected
          )
        }
      })
  }

  ngOnInit() {
    this.isLoading$ = combineLatest([
      this.store.select(memberSelectors.selectIsLoading),
      this.store.select(userRoleSelectors.selectIsLoading)
    ]).pipe(map(([a, b]) => a || b))

    this.members$ = this.store.select(memberSelectors.selectAll)
    this.organizationModuleConfig =
      environment.moduleConfigs.organizationModule.isActive

    this.payments$ = this.store.select(paymentSelectors.selectEntities)
    this.saleItems$ = this.store.select(saleItemSelectors.selectEntities)
    this.products$ = this.store.select(productSelectors.selectEntities)
    this.bookings$ = this.store.select(bookingSelectors.selectEntities)
    this.participations$ = this.store.select(
      participationSelectors.selectEntities
    )
    this.subscriptions$ = this.store.select(
      subscriptionSelectors.selectEntities
    )
    this.subscribers$ = this.store.select(subscriberSelectors.selectAll)
    this.multidayEnabled$ = this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType<boolean>(
          ConfigOptionsKeys.MULTIDAY_ENABLED
        )
      )
      .pipe(map(Boolean))

    this.form = this.fb.group({
      statusCode: [StatusCode.INFLIGHT, [Validators.required]],
      customerPersonId: [null, [Validators.required]],
      organizationId: [],
      salesPersonId: null,
      internalNote: [],
      orderNumber: []
    })

    this.payments$
      .pipe(
        untilDestroyed(this),
        withLatestFrom(this.store.select(saleSelectors.selectIdRouteParam))
      )
      .subscribe(([payments, saleIdParam]) => {
        let saleId = this.sale?.id || Number(saleIdParam)
        if (!saleId) {
          return false
        }

        this.saleHaveUndecidedPayments$.next(
          Object.values(payments).some(
            (payment) =>
              payment.saleId === saleId &&
              payment.payMethod === PayMethodType.PAY_METHOD_UNDECIDED
          )
        )
      })

    this.store
      .select(saleSelectors.selectIdRouteParam)
      .pipe(take(1))
      .subscribe((saleId) => {
        if (!saleId) {
          this.form.addControl('customerCategory', new UntypedFormControl(null))
        }
      })
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )
    this.currentUserRole$ = this.store.select(
      persistentUserRoleSelectors.selectSelectedUserRole
    )

    this.currentUserRole$
      .pipe(untilDestroyed(this))
      .subscribe(
        (currentUserRole) =>
          (this.currentUserRole =
            currentUserRole && currentUserRole.siteId
              ? currentUserRole
              : { ...currentUserRole, siteId: environment.defaultSiteId })
      )

    this.categories$ = this.selectedLanguage$.pipe(
      switchMap((language) => {
        return this.store.select(configurationOptionSelectors.selectAll).pipe(
          map((categories) => {
            let categoriesOptions = []
            for (let category = 0; category < 10; category++) {
              const categoryDescription = categories.find(
                (cat) =>
                  cat.key === `${language}_category${category}desc` &&
                  (cat.siteId === this.currentUserRole?.siteId ||
                    !cat.siteId) &&
                  cat.value?.length
              )
              categoryDescription
                ? categoriesOptions.push(categoryDescription)
                : null
            }
            let mappedCategeries = JSON.parse(JSON.stringify(categoriesOptions))
            mappedCategeries.map((item) => {
              if (Number(item.siteId) != 0 && !item.siteId) {
                item.siteId = this.currentUserRole.siteId
              }
            })
            categoriesOptions = JSON.parse(JSON.stringify(mappedCategeries))
            return categoriesOptions
          }),
          map((configOptions) => {
            let nCategories = environment.moduleConfigs.hasExtendedCategories
              ? 9
              : 4
            return configOptions
              .filter(
                (c, i) =>
                  (c.siteId == this.currentUserRole?.siteId &&
                    i <= nCategories) ||
                  (!c.siteId && i <= nCategories)
              )
              .map((option) => option.key)
              .map((key) => key.split(language + '_').pop())
          })
        )
      })
    )
    this.store
      .select(paymentSelectors.selectEntities)
      .pipe(untilDestroyed(this))
      .subscribe((paymentEntities) => {
        let saleIsAdjustable = false
        if (this.sale?.payments?.length) {
          let isUnpaidPayment = false
          let salePaymentsTotalAmount = 0
          this.sale?.payments?.forEach((paymentId) => {
            salePaymentsTotalAmount += Number(
              paymentEntities[paymentId]?.amount
            )
            if (paymentEntities[paymentId]?.collectStatus != 0) {
              isUnpaidPayment = true
            }
          })
          if (
            salePaymentsTotalAmount < Number(this.sale?.total) ||
            isUnpaidPayment ||
            (salePaymentsTotalAmount === this.sale?.total &&
              this.sale?.total === 0)
          ) {
            saleIsAdjustable = true
          }
        } else {
          saleIsAdjustable = true
        }
        if (!saleIsAdjustable) {
          Object.keys(this.form.controls).forEach((key) => {
            if (
              !(
                (key == 'internalNote' || key == 'orderNumber') &&
                this.sale &&
                this.saleService.getFinalStatus(this.sale) ===
                  this.saleStatusCode.DONE
              )
            ) {
              this.form.controls[key].disable()
            } else {
              this.form.get(key).enable()
            }
          })
        }
      })

    this.organizationEntities$ = this.store.select(
      organizationSelectors.selectEntities
    )
    this.roleEntities$ = this.store.select(roleSelectors.selectEntities)

    this.store
      .select(authSelectors.selectMember)
      .pipe(take(1))
      .subscribe((member) => {
        member?.id
          ? this.form.controls.salesPersonId.patchValue(member.id)
          : null
      })

    this.triggerSubmit$
      ?.pipe(untilDestroyed(this))
      .subscribe((shouldSubmit) => {
        if (shouldSubmit === undefined) {
          return
        }

        this.onFormSubmit()
      })

    if (this?.sale?.customerPersonId) {
      this.loadMemberOrganizations(this.sale?.customerPersonId)
    }

    this.userByRoles$.pipe(untilDestroyed(this)).subscribe((users) => {
      if (this.selectedOrganization) {
        if (Object.values(users)[0]?.length)
          this.store.dispatch(
            memberActions.fetchMany({
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'userRoles' },
                  { field: 'userRoles.organization' },
                  { field: 'roles' },
                  { field: 'supervisorSkills' }
                ],
                search: {
                  id: { [CondOperator.IN]: Object.values(users)[0] }
                }
              })
            })
          )
      }
    })

    if (this.sale?.customerPersonId) {
      this.getMemberCategory(this.sale?.customerPersonId)
    }
  }

  public orderNumberSave() {
    this.store.dispatch(
      saleActions.updateOneById({
        id: this.sale.id,
        params: {
          orderNumber: this.form.controls['orderNumber'].value
        }
      })
    )
    this.notificationService.emit('alerts.success', 'success')
  }
  public internalNotesSave() {
    this.store.dispatch(
      saleActions.updateOneById({
        id: this.sale.id,
        params: {
          internalNote: this.form.controls['internalNote'].value
        }
      })
    )
    this.notificationService.emit('alerts.success', 'success')
  }
  public orderNumberChange() {
    this.orderNumbersChange = true
  }
  public internalNoteChange() {
    this.internalNotesChange = true
  }

  ngOnChanges(changes: SimpleChanges) {
    this.selectedMember = null
    if (
      JSON.stringify(changes['sale']?.currentValue) !==
      JSON.stringify(changes['sale']?.previousValue)
    ) {
      this.sale = changes['sale'].currentValue
    }
  }
  onFormSubmit() {
    if (this.form.invalid) {
      console.log('Form invalid')
      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty()
      })
      scrollToFirstInvalidControl(this.elementRef, this.notificationService)

      return
    } else {
      if (!!this.sale) {
        this.onSubmitWithSale()
      } else {
        this.onSubmit.emit(this.form.value)
      }
    }
  }
  private onSubmitWithSale(): void {
    this.onSubmit.emit(this.form.value)
  }

  public onSearchMembers(term: string): void {
    this.store.dispatch(
      memberActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            $or: [
              {
                firstName: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                email: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              },
              {
                lastName: {
                  [CondOperator.CONTAINS_LOW]: term
                }
              }
            ]
          },
          join: [
            { field: 'userRoles' },
            { field: 'userRoles.organization' },
            { field: 'subscribers' }
          ]
        })
      })
    )
  }

  public getMemberName$(id): Observable<string> {
    if (!id) {
      return of('')
    }
    return this.store
      .select(memberSelectors.selectEntities)
      .pipe(
        map((members) =>
          members[Number(id)]
            ? `${members[Number(id)]?.firstName} ${
                members[Number(id)]?.lastName
              }`
            : ''
        )
      )
  }

  public changeCustomerbutton(): Observable<string> {
    this.changeCustomer = true
    if (!this.selectedOrganization) {
      return of('')
    } else {
      this.customers = []
      this.store
        .select(userRoleSelectors.selectsearchedUsers)
        .pipe(untilDestroyed(this), take(1))
        .subscribe((users) => {
          this.store
            .select(memberSelectors.selectEntities)
            .pipe(take(1))
            .subscribe((members) => {
              users[this.selectedOrganization].map((item) => {
                let customer: Customer = {
                  id: null,
                  firstName: null,
                  lastName: null
                }
                customer.id = item
                customer.firstName = members[item]?.firstName
                customer.lastName = members[item]?.lastName
                this.customers.push(customer)
              })
            })
        })
    }
  }

  public getOrganisationName$(id): Observable<string> {
    if (!id) {
      return of('')
    }
    return this.store
      .select(organizationSelectors.selectEntities)
      .pipe(
        map((organisations) =>
          organisations[Number(id)]
            ? organisations[Number(id)]?.description
            : ''
        )
      )
  }
  getCategoryWithValue(categoryIdArray) {
    let mappedCategoryIdArray = []
    categoryIdArray?.map((categoryId) => {
      this.configService.selectedLang$
        .pipe(
          take(1),
          switchMap((lang) => {
            return this.store.select(
              configurationOptionSelectors.selectByKey,
              `${lang}_category${categoryId}desc`
            )
          }),
          filter((configurationOption) => Boolean(configurationOption)),
          map((configurationOption) => configurationOption.value)
        )
        .subscribe((category) => {
          if (
            category &&
            category.trim().length > 0 &&
            category.toLowerCase() != 'null' &&
            category.toLowerCase() != 'undefined'
          ) {
            mappedCategoryIdArray.push(categoryId)
          }
        })
    })

    this.mappedCategoryIdArray = mappedCategoryIdArray
  }
  getMemberCategory(memberId) {
    this.members$.pipe(take(1)).subscribe((item) => {
      const memberCategory = item.filter((item: any) => item.id === memberId)[0]
        ?.category
      this.memberCategory =
        memberCategory && memberCategory !== 0
          ? [memberCategory]
          : [1, 2, 3, 4, 5, 6, 7, 8, 9]
      this.getCategoryWithValue(this.memberCategory)
      const viewSelected =
        JSON.stringify([1, 2, 3, 4, 5, 6, 7, 8, 9]) ===
        JSON.stringify(this.memberCategory)
      this.defaultSelected = viewSelected ? null : this.memberCategory[0]
      if (this.form.controls['customerCategory']) {
        this.defaultSelected = !this.defaultSelected ? 0 : this.defaultSelected
        this.form.controls['customerCategory'].patchValue(this.defaultSelected)
      }
    })
  }

  onChangeMemberSelect(memberId: number): void {
    this.defaultSelected = null
    this.memberCategory = null
    this.getMemberCategory(memberId)
    this.form.controls['organizationId'].setValue(undefined)
    this.loadMemberOrganizations(memberId)
  }

  loadMemberOrganizations(memberId: number): void {
    if (memberId) {
      this.store.dispatch(
        subscriberActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              memberId: memberId
            },
            join: [
              { field: 'subscription' },
              { field: 'subscription.translations' }
            ]
          })
        })
      )
      this.store.dispatch(
        userRoleActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: { userId: memberId },
            join: [{ field: 'role' }, { field: 'organization' }]
          })
        })
      )
    }

    this.userRoles$ = combineLatest([
      this.store.select(userRoleSelectors.selectByFilter, {
        field: 'userId',
        value: memberId
      }),
      this.store.select(organizationSelectors.selectEntities)
    ]).pipe(
      map(([userRoles, organizationEntities]) => {
        const usersRole = userRoles
          .sort((a, b) => (a.organizationId < b.organizationId ? 1 : -1))
          .filter(
            (userRole) =>
              userRole.organizationId &&
              userRole.isApproved === true &&
              organizationEntities[userRole.organizationId]?.isActive
          )
        return usersRole
      })
    )
    this.userRoles$
      .pipe(
        untilDestroyed(this),
        map((userRoles: any) => {
          if (userRoles.length > 0) {
            return [...new Set(userRoles.map((item) => item.organizationId))]
          }
        })
      )
      .subscribe()
  }
  public onMemberClick(member: Member): void {
    this.selectedMember = member
  }
  public clickAbort(): void {
    this.changeCustomer = false
    this.selectedMember = null
  }
  public clickSuccess(): void {
    this.changeCustomer = false
    if (this.selectedMember?.id !== this.sale?.customerPersonId) {
      this.form.get('customerPersonId').setValue(this.selectedMember.id)
      this.form.markAsDirty()
    }
  }
  public onChangeOrganization(organization): void {
    // only use for selected sale at the moment
    if (!!organization && this.sale) {
      this.store
        .select(userRoleSelectors.selectOrganization)
        .pipe(
          switchMap((org: number[]) => {
            if (!org.some((item) => item === organization)) {
              this.store.dispatch(
                userRoleActions.upsertManyByOrganization({ organization })
              )
              return of(organization)
            } else {
              return of(null)
            }
          })
        )
        .subscribe((organiza) => {})
    }
  }

  public onClickUpdatePrices() {
    const message = this.translate.instant('saleModule.updatePricesForThisSale')
    if (!confirm(message)) {
      return
    }
    this.isUpdatingPrices = true
    this.store.dispatch(
      saleActions.updateOneById({
        id: this.sale.id,
        params: {
          action: SalePatchActions.UPDATE_PRICES
        }
      })
    )

    this.actions$
      .pipe(
        ofType(saleActions.upsertMany),
        take(1),
        timeout(10000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        this.isUpdatingPrices = false

        action
          ? this.notificationService.emit('alerts.success', 'success')
          : console.error(action)
      })
  }

  public getCategoryValue(categoryDescription: string): number {
    const regex = /\d+/

    return Number(categoryDescription.match(regex)?.[0])
  }
}
